import { Routes, Route } from 'react-router-dom';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { Suspense, lazy } from 'react';
import { ThemeContextProvider } from './contexts/ThemeContext';
import { AppProvider } from './contexts/AppContext';
import { Header, Footer } from './components';
import ScrollToTop from './components/common/ScrollToTop';
import ErrorBoundary from './components/common/ErrorBoundary';
import NotificationSystem from './components/common/NotificationSystem';

// Lazy load pages for better performance
const HomePage = lazy(() => import('./pages/HomePage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const JoinUsPage = lazy(() => import('./pages/JoinUsPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));

// Loading component
const PageLoader = () => (
  <Box 
    sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '50vh' 
    }}
  >
    <CircularProgress size={40} />
  </Box>
);

function App() {
  return (
    <ErrorBoundary>
      <AppProvider>
        <ThemeContextProvider>
          <CssBaseline />
          <Box sx={{ position: 'relative' }}>
            <Header />
            <Box 
              component="main" 
              sx={{ 
                position: 'relative',
                zIndex: 0,
                pt: 0,
                minHeight: '100vh',
                backgroundColor: 'background.default'
              }}
            >
              <ScrollToTop />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/services" element={<ServicesPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/join" element={<JoinUsPage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/security" element={<SecurityPage />} />
                  {/* Admin route - restricted access */}
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="/admin/*" element={<AdminPage />} />
                  {/* Catch-all route for 404 - must be last */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Suspense>
            </Box>
            <Footer />
            <NotificationSystem />
          </Box>
        </ThemeContextProvider>
      </AppProvider>
    </ErrorBoundary>
  );
}

export default App;